/**
 * Voluum-style traffic flow — steps from destination + transition (panel parity).
 * For Media Devoted | Created By Ciro Autiero
 */

import { Fragment, type FC, type ReactNode } from 'react';
import {
  ArrowRight,
  LayoutGrid,
  Megaphone,
  Package,
  RefreshCw,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { VoluumOfferRedirectMode } from '@trafficpulse/shared';

import voluumLogo from '@/assets/traffic/voluum logo.png';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { usePathTransitionTrackerName } from '@/features/flow-optimizer/tabs/standard/v2/paths/settings/path-transition/path-transition-branding';
import { cn } from '@/lib/utils';

import type { PathDestinationMode } from '../path-settings.constants';
import {
  getPathTransitionFlowSegments,
  pathFlowSegmentLabels,
  type PathFlowSegment,
} from './path-transition-flow-segments';

export interface PathTransitionFlowStripProps {
  mode: VoluumOfferRedirectMode;
  compact?: boolean;
  destination?: PathDestinationMode;
}

function iconChip(
  Icon: LucideIcon,
  label: string,
  compact: boolean,
  title?: string,
): ReactNode {
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded border border-border/70 bg-background px-0.5 py-px text-foreground shadow-sm"
      title={title ?? label}
    >
      <Icon
        className={cn('text-primary', compact ? 'h-2.5 w-2.5' : 'h-3 w-3')}
        strokeWidth={1.75}
        aria-hidden
      />
      <span className={cn('font-semibold', compact && 'text-[10px]')}>
        {label}
      </span>
    </span>
  );
}

function segmentNode(seg: PathFlowSegment, compact: boolean, trackerName: string | null): ReactNode {
  switch (seg.kind) {
    case 'ad':
      return iconChip(Megaphone, 'Ad', compact);
    case 'voluum':
      if (trackerName) return <ProviderBadge provider="tres-commas" noun="tracker" className="px-0.5 py-px" />;
      return (
        <span className="inline-flex items-center rounded border border-border/70 bg-background px-0.5 py-px shadow-sm">
          <img
            src={voluumLogo}
            alt=""
            className={cn(
              'object-contain',
              compact ? 'h-2.5 w-2.5' : 'h-3 w-3',
            )}
            width={compact ? 10 : 12}
            height={compact ? 10 : 12}
            aria-hidden
          />
        </span>
      );
    case 'lander':
      return iconChip(LayoutGrid, 'Lander', compact);
    case 'meta':
      return iconChip(RefreshCw, 'Meta', compact, 'Meta refresh');
    case 'offer':
      return iconChip(Package, 'Offer', compact);
  }
}

function flowRow(
  segments: PathFlowSegment[],
  compact: boolean,
  trackerName: string | null,
): ReactNode {
  return segments.map((seg, i) => (
    <Fragment key={`${seg.kind}-${i}`}>
      {i > 0 ? (
        <ArrowRight
          className={cn(
            'shrink-0 text-muted-foreground/70',
            compact ? 'h-2.5 w-2.5' : 'h-3 w-3',
          )}
          aria-hidden
        />
      ) : null}
      {segmentNode(seg, compact, trackerName)}
    </Fragment>
  ));
}

const PathTransitionFlowStrip: FC<PathTransitionFlowStripProps> = ({
  mode,
  compact = false,
  destination = 'LANDERS_AND_OFFERS',
}) => {
  const trackerName = usePathTransitionTrackerName();
  const segments = getPathTransitionFlowSegments(mode, destination);
  const summary = pathFlowSegmentLabels(segments).join(', ').replace('Voluum', trackerName ?? 'Voluum');
  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-muted/20 text-muted-foreground',
        compact ? 'px-2 py-1 text-[10px] leading-snug' : 'px-3 py-2 text-[11px] leading-snug',
      )}
      aria-live="polite"
    >
      <p className="m-0">
        <span className="font-semibold text-foreground">
          Your traffic&apos;s flow:
        </span>{' '}
        <span
          className="inline-flex flex-wrap items-center gap-x-0.5 gap-y-0.5 align-middle"
          aria-label={`Your traffic flow: ${summary}`}
        >
          {flowRow(segments, compact, trackerName)}
        </span>
      </p>
    </div>
  );
};

export default PathTransitionFlowStrip;
